import { Form } from 'components';
import { MediaUpload } from 'components/Form/inputs/MediaUpload/MediaUpload';
import ChoiceModal from 'pages/ModulePage/components/ChoiceModal/ChoiceModal';
import ItemFormModal from 'pages/ModulePage/components/ItemFormModal/ItemFormModal';
import SelectedItems from 'pages/ModulePage/components/SelectedItems/SelectedItems';
import SelectItemsModal from 'pages/ModulePage/components/SelectItemsModal/SelectItemsModal';
import { useFormikContext } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildArtistFromForm,
  canSaveArtistForm,
  filterArtistByNameOrEmail,
  getArtistDescription,
  getArtistFormInitialValues,
  getArtistImage,
  getArtistKey,
  getArtistName,
  isCustomArtist,
} from './utils';

const ArtistCustomFormFields = () => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });

  return (
    <>
      <Form.GroupVertical required label={t('name')}>
        <Form.Input name="name" type="text" />
      </Form.GroupVertical>
      <Form.GroupVertical required label={t('artistPicture')}>
        <MediaUpload name="picture" imageField="picture" maxFiles={1} withCrop aspectRatio={1} />
      </Form.GroupVertical>
      <Form.GroupVertical label={t('description')}>
        <Form.Input name="description" type="text" />
      </Form.GroupVertical>
    </>
  );
};

const getArtistItemCardProps = (artist, { t }) => {
  const name = getArtistName(artist);
  const description = getArtistDescription(artist);

  return {
    imageSrc: getArtistImage(artist),
    imageAlt: name || 'Artist',
    badge: isCustomArtist(artist) ? t('customBadge') : null,
    title: name || null,
    meta: description ? [{ value: description }] : [],
  };
};

export const createArtistsConfig = (useAvailableItems) => {
  const ArtistsConfig = () => {
    const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
    const { t: tApp } = useTranslation('app');
    const { values, setFieldValue } = useFormikContext();
    const { items, isLoading } = useAvailableItems();

    const customItems = useMemo(() => values.customItems || [], [values.customItems]);
    const selectedItems = useMemo(() => values.selectedItems || [], [values.selectedItems]);

    const [chooseOpen, setChooseOpen] = useState(false);
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [librarySearch, setLibrarySearch] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const isEditing = Boolean(editingItem);

    const currentLibrarySelected = useMemo(
      () => selectedItems.filter((artist) => !isCustomArtist(artist)),
      [selectedItems],
    );

    const filteredLibraryItems = useMemo(() => {
      if (!librarySearch.trim()) return items;
      return items.filter((item) => filterArtistByNameOrEmail(item, librarySearch.trim()));
    }, [items, librarySearch]);

    const handleRemove = (item) => {
      const key = getArtistKey(item);
      if (isCustomArtist(item)) {
        setFieldValue(
          'customItems',
          customItems.filter((artist) => getArtistKey(artist) !== key),
        );
      }
      setFieldValue(
        'selectedItems',
        selectedItems.filter((artist) => getArtistKey(artist) !== key),
      );
    };

    const handleLibraryConfirm = (nextLibrarySelected) =>
      setFieldValue('selectedItems', [...customItems, ...nextLibrarySelected]);

    const handleFormConfirm = (artist) => {
      const key = getArtistKey(artist);
      const isEdit = customItems.some((item) => getArtistKey(item) === key);
      const nextCustom = isEdit
        ? customItems.map((item) => (getArtistKey(item) === key ? artist : item))
        : [artist, ...customItems];
      const nextSelected = isEdit
        ? selectedItems.map((item) => (getArtistKey(item) === key ? artist : item))
        : [artist, ...selectedItems];
      setFieldValue('customItems', nextCustom);
      setFieldValue('selectedItems', nextSelected);
    };

    const closeFormModal = () => {
      setFormOpen(false);
      setEditingItem(null);
    };

    return (
      <>
        <Form.GroupHorizontal label={t('selectedItems')}>
          <SelectedItems
            isLoading={isLoading}
            items={selectedItems}
            setItems={(next) => setFieldValue('selectedItems', next)}
            getItemKey={getArtistKey}
            getItemCardProps={getArtistItemCardProps}
            onOpenModal={() => setChooseOpen(true)}
            onRemove={handleRemove}
            onEdit={(item) => {
              setEditingItem(item);
              setFormOpen(true);
            }}
            isEditable={isCustomArtist}
          />
        </Form.GroupHorizontal>

        <ChoiceModal
          isOpen={chooseOpen}
          onClose={() => setChooseOpen(false)}
          title={t('addArtist')}
          options={[
            {
              id: 'existing',
              icon: 'bi bi-people',
              title: t('existingArtist'),
              description: t('existingArtistDesc'),
            },
            {
              id: 'custom',
              icon: 'bi bi-person-plus',
              title: t('customArtist'),
              description: t('customArtistDesc'),
            },
          ]}
          onSelect={(id) => {
            setChooseOpen(false);
            if (id === 'existing') {
              setLibraryOpen(true);
            } else if (id === 'custom') {
              setEditingItem(null);
              setFormOpen(true);
            }
          }}
        />

        <SelectItemsModal
          isOpen={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          onSelect={handleLibraryConfirm}
          initialSelected={currentLibrarySelected}
          items={filteredLibraryItems}
          isLoading={isLoading}
          getItemKey={getArtistKey}
          getItemCardProps={getArtistItemCardProps}
          onSearch={setLibrarySearch}
        />

        <ItemFormModal
          isOpen={formOpen}
          onClose={closeFormModal}
          title={isEditing ? t('editArtist') : t('addArtist')}
          saveText={isEditing ? t('saveArtist') : t('addArtist')}
          cancelText={tApp('cancel')}
          formKey={editingItem ? getArtistKey(editingItem) : 'new'}
          initialValues={getArtistFormInitialValues(editingItem)}
          onSubmit={(formValues) => {
            handleFormConfirm(buildArtistFromForm(formValues));
            closeFormModal();
          }}
          canSave={canSaveArtistForm}
        >
          {() => <ArtistCustomFormFields />}
        </ItemFormModal>
      </>
    );
  };

  return ArtistsConfig;
};
